import React from 'react';
import { Box, Button, Carousel, Text } from '@stitch-fix/mode-react';
import { G } from '@mobily/ts-belt';
import classNames from 'classnames';
import { GetStartedButton } from '../../../../components/GetStartedButton';
import { DynamicAdsImage, type DynamicAdsImageProps } from './DynamicAdsImage';
import styles from './style.module.scss';
import type { ContentStackJSONRichText } from '../../../../../scripts/schemaGenerator/fields/rte';
import { isNonEmptyString } from '../../../../utils/helpers';
import { isEmpty, JsonRteRenderer } from '../../../../components/JsonRteRenderer/JsonRteRenderer';
import { useStickyCtaContext } from '../../../../components/StickyCta/useStickyCtaContext';
import { buttonColorToButtonVariant } from '../../../../components/ModuleFooter/ModuleFooter';
import type { ModularComponentProps } from '../../../../components/ModularPageWrapper/ModularPageWrapper';
import { useFrontDoorOffer } from '../../../../hooks/useFrontDoorOffer';
const imageAspectRatio = {
  sm: 327,
  xl: 417
};
export interface DynamicAdsHeroProps {
  fontColor?: 'white' | 'blue-45' | 'gray-16' | 'forest-60' | 'grapefruit-60';
  buttonColor?: 'mint' | 'navy' | 'citrus' | 'holiday forest' | 'holiday grapefruit' | 'holiday snow';
  alignContent?: 'left' | 'center' | 'right';
  ctaHref?: string;
  ctaText?: string;
  description: string | ContentStackJSONRichText;
  eyebrow?: string;
  title: string | ContentStackJSONRichText;
  images: DynamicAdsImageProps['image'][];
  enableOfferOverride?: boolean;
}
const HeroText = ({
  title,
  eyebrow,
  description,
  alignContent,
  fontColor
}: Pick<DynamicAdsHeroProps, 'title' | 'eyebrow' | 'description' | 'alignContent' | 'fontColor'>) => {
  return <>
      {isNonEmptyString(eyebrow) && <Text setting="eyebrow-medium" as="h1" className={styles.eyebrow} color={fontColor} align={alignContent}>
          {eyebrow}
        </Text>}
      {G.isString(title) ? <Text setting="title-xlarge" as={isNonEmptyString(eyebrow) ? 'h2' : 'h1'} className={styles.title} color={fontColor} align={alignContent}>
          {title}
        </Text> : <JsonRteRenderer data={title} options={{
      textProps: {
        h1: {
          color: fontColor,
          align: alignContent,
          spacingBottom: false
        }
      }
    }} />}
      {G.isString(description) ? <Text className={styles.description} align={alignContent} as="p" color={fontColor}>
          {description}
        </Text> : !isEmpty(description?.children) && <JsonRteRenderer data={description} options={{
      textProps: {
        p: {
          color: fontColor,
          align: alignContent,
          spacingBottom: false
        },
        li: {
          color: fontColor,
          align: alignContent,
          spacingBottom: false
        }
      }
    }} />}
    </>;
};
export const DynamicAdsHero = ({
  ctaHref = '/signup',
  ctaText = 'Take your style quiz',
  eyebrow,
  title,
  description,
  images,
  buttonColor = 'citrus',
  alignContent = 'left',
  fontColor = 'blue-45',
  verticalSpacing,
  enableOfferOverride = false
}: DynamicAdsHeroProps & ModularComponentProps) => {
  const {
    isThirtyDollarTreatment,
    offerAmount
  } = useFrontDoorOffer(!enableOfferOverride);
  const CtaButtonOverride = isThirtyDollarTreatment && enableOfferOverride ? <Button as="a" variant="filled-statement" href="/signup">
        Take the quiz, get ${offerAmount} off
      </Button> : null;
  const {
    setStickyContainerTarget
  } = useStickyCtaContext();
  return <Box className={styles.container} data-testid="dynamic-ads-hero" ref={setStickyContainerTarget} mt={verticalSpacing}>
      <div className={classNames(styles.content, styles[alignContent])}>
        <HeroText title={title} eyebrow={eyebrow} description={description} alignContent={alignContent} fontColor={fontColor} />
        <div className={styles.cta}>
          {CtaButtonOverride || <GetStartedButton copy={ctaText} href={ctaHref} eventLocation="hero" variant={buttonColorToButtonVariant(buttonColor)} />}
        </div>
      </div>
      <div className={styles.media}>
        <Carousel ariaLabel="Dynamic Ads Carousel" slideWidth={{
        sm: `${imageAspectRatio.sm}px`,
        xl: `${imageAspectRatio.xl}px`
      }} gutterOverflow={{
        sm: 1.5,
        md: 3,
        lg: 0
      }}>
          {images.map((image, index) => <DynamicAdsImage key={image.src} index={index} image={image} aspectRatio={imageAspectRatio} />)}
        </Carousel>
      </div>
    </Box>;
};